import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, Swatch } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { BottomDock } from "@/components/BottomDock";
import { NewMessageModal } from "@/components/NewMessageModal";
import { useConversations } from "@/hooks/useMessages";

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function Messages() {
  const nav = useNavigate();
  const { rows, loading } = useConversations();
  const [composerOpen, setComposerOpen] = useState(false);

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 100 }}>
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            // Always route to /you, not nav(-1). When you go
            // Profile → Messages → Thread → back-to-Messages, the
            // browser-history back button on the Messages page wants
            // to return to the thread you just left — a loop. Routing
            // explicitly to /you breaks the cycle.
            onClick={() => nav("/you")}
            aria-label="Back to profile"
            style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Inbox</span>
          <button
            onClick={() => setComposerOpen(true)}
            aria-label="New message"
            style={{
              background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--ink))",
              width: 30, height: 30,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 18,
              lineHeight: 1, paddingBottom: 2,
            }}
          >
            +
          </button>
        </div>

        <div style={{ padding: "10px 20px 0" }}>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>Private notes</span>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 44, color: "hsl(var(--ink))", letterSpacing: "-0.03em", lineHeight: 0.9, marginTop: 4 }}>
            Messages
          </div>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, color: "hsl(var(--ink-2))", marginTop: 4 }}>
            collector-to-collector notes — keep it tidy here, not in the comments
          </div>
        </div>

        <div style={{ padding: "18px 20px 0", display: "flex", flexDirection: "column", gap: 8 }}>
          {loading && (
            <div className="caps" style={{ color: "hsl(var(--ink-2))", padding: "20px 0", textAlign: "center" }}>
              Loading…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div style={{ background: "hsl(var(--cream))", border: "1px dashed hsl(var(--ink))", padding: "20px 14px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15, color: "hsl(var(--ink-2))" }}>
                No conversations yet!
              </div>
            </div>
          )}
          {rows.map((c) => {
            const isGeneral = !c.scan_id;
            return (
              <button
                key={c.id}
                onClick={() => nav(`/messages/${c.id}`)}
                style={{
                  background: c.unread ? "hsl(var(--cream))" : "transparent",
                  border: c.unread ? "1.5px solid hsl(var(--ink))" : "1px solid hsl(var(--ink))",
                  padding: "12px 12px",
                  cursor: "pointer",
                  display: "flex",
                  gap: 10,
                  alignItems: "stretch",
                  textAlign: "left",
                  position: "relative",
                }}
              >
                {isGeneral ? (
                  <Swatch
                    kind={c.other_avatar}
                    style={{ width: 56, height: 56, borderRadius: "50%", border: "1px solid hsl(var(--ink))", flexShrink: 0 }}
                  />
                ) : c.scan_photo ? (
                  <img
                    src={c.scan_photo}
                    alt={c.scan_title}
                    style={{ width: 56, height: 56, objectFit: "cover", border: "1px solid hsl(var(--ink))", flexShrink: 0 }}
                  />
                ) : (
                  <Swatch kind={c.scan_img as never} style={{ width: 56, height: 56, border: "1px solid hsl(var(--ink))", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
                    <span style={{
                      fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: c.unread ? 900 : 700, fontSize: 14, lineHeight: 1.1,
                      color: "hsl(var(--ink))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      @{c.other_handle}
                    </span>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: "hsl(var(--ink-2))", letterSpacing: "0.06em", flexShrink: 0 }}>
                      {timeAgo(c.last_message_at)}
                    </span>
                  </div>
                  {!isGeneral && c.scan_title && (
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "hsl(var(--rust))", letterSpacing: "0.06em", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      RE: {c.scan_title}
                    </div>
                  )}
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
                    color: c.unread ? "hsl(var(--ink))" : "hsl(var(--ink-2))",
                    marginTop: 4, lineHeight: 1.3,
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                  }}>
                    {c.last_body ?? "No messages yet — say hi."}
                  </div>
                </div>
                {c.unread && (
                  <span style={{
                    position: "absolute", top: 10, right: 10,
                    width: 8, height: 8, borderRadius: "50%",
                    background: "hsl(var(--rust))",
                  }} />
                )}
              </button>
            );
          })}
        </div>
      </div>
      <NewMessageModal open={composerOpen} onClose={() => setComposerOpen(false)} />
      <BottomDock />
    </MobileFrame>
  );
}